/**
 * SHALL SHARK — bootstrap. Owns the canvas, the frame, the run seed, and the active mode.
 * Everything else is a system that this file wires together.
 */
import './style.css';

import { Loop } from './core/Loop.ts';
import { Input } from './core/Input.ts';
import { hashString } from './core/Rng.ts';
import { bus } from './core/Events.ts';
import { Renderer } from './render/Renderer.ts';
import { GameCamera } from './render/Camera.ts';
import { MeshService } from './voxel/MeshService.ts';
import { DebugOverlay, type DebugSnapshot } from './ui/Debug.ts';
import { Menu } from './ui/Menu.ts';
import { TouchControls } from './ui/TouchControls.ts';
import { Landing, bayFromUrl } from './ui/Landing.ts';
import { defaultSpec } from './voxel/SharkForge.ts';
import { SharkPreview } from './render/SharkPreview.ts';
import { Reel, parseFormat, FORMATS, REEL_SECONDS, type ReelFormat } from './reel/Reel.ts';
import { Recorder, REEL_FPS, recordingSupported } from './reel/Recorder.ts';
import { AudioEngine } from './audio/AudioEngine.ts';
import { Soundscape } from './audio/Soundscape.ts';
import { NetClient } from './net/NetClient.ts';
import { SwimMode } from './modes/SwimMode.ts';
import { LabMode } from './modes/LabMode.ts';
import type { Mode, ModeContext } from './modes/Mode.ts';

/**
 * The reef to build.
 *
 * A bay OUTRANKS an explicit ?seed=, and has to: everyone in a bay must be in the same ocean,
 * and the code is the only thing all of them share. A stale seed left in a pasted link would
 * otherwise put one player on a reef of their own with everybody else's sharks swimming
 * through terrain that is not there.
 */
function readSeed(bay: string): number {
  if (bay !== '') return NetClient.seedForBay(bay);
  const raw = new URLSearchParams(location.search).get('seed');
  if (raw === null || raw.length === 0) {
    // a fresh run seed, but one the player can read off the debug overlay and share
    return (Math.floor(performance.now() * 1000) ^ 0x9e3779b9) >>> 0;
  }
  const asNumber = Number(raw);
  return Number.isFinite(asNumber) && raw.trim() !== '' ? asNumber >>> 0 : hashString(raw);
}

function fatal(message: string): void {
  const el = document.createElement('div');
  el.className = 'fatal';
  el.textContent = message;
  document.body.appendChild(el);
  console.error(message);
}

async function boot(): Promise<void> {
  const canvas = document.getElementById('gl') as HTMLCanvasElement | null;
  const overlay = document.getElementById('overlay') as HTMLElement | null;
  if (!canvas || !overlay) {
    fatal('SHALL SHARK: index.html is missing #gl or #overlay.');
    return;
  }

  const renderer = new Renderer(canvas);
  const camera = new GameCamera(renderer.width / Math.max(1, renderer.height));
  const input = new Input(canvas);
  const mesh = new MeshService();
  const debug = new DebugOverlay(overlay);

  /* Audio. Built up front and left silent: a browser will not run an AudioContext until a
     real user gesture, so the graph exists from boot and simply waits. First key or click
     unlocks it and starts the scheduler. */
  const audio = new AudioEngine();
  const soundscape = new Soundscape(audio);
  const unlock = (): void => {
    /* Started REGARDLESS of what resume reports. start() is idempotent and safe before the
       context is running, and gating it on the resume result is what left phones silent: a
       browser that resumes a moment later would never have had its scheduler started. */
    void audio.unlock().finally(() => soundscape.start());
  };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);

  const ctx: ModeContext = {
    scene: renderer.scene,
    camera,
    input,
    mesh,
    gl: renderer.renderer,
    overlay,
    pixelRatio: () => renderer.pixelRatio,
    audio: soundscape,
  };
  const swim = new SwimMode(ctx);
  const lab = new LabMode(ctx);

  /* The teaser reel takes over the whole boot: fixed canvas size, no landing page, a scripted
     camera and a recorder. See docs/teaser-script.md — this file plays that document. */
  const reelFormat = parseFormat(new URLSearchParams(location.search).get('reel'));
  if (reelFormat !== null) {
    await runReel(reelFormat, canvas, overlay);
    return;
  }

  /* Nothing starts until the player has chosen. Clicking a landing button is also the user
     gesture the browser demands before an AudioContext will run, so the choice and the audio
     unlock are the same event rather than two things that have to be arranged. */
  const landing = new Landing(overlay, new NetClient().enabled);
  const preview = new SharkPreview();
  // Drag, right-drag and scroll go to the canvas; the panel keeps its own clicks.
  preview.attach(canvas);

  /* The turntable draws into the GAME's canvas, behind the landing panel. One context, one
     surface: a second canvas would mean a second WebGL context — a real cost on integrated
     graphics, and browsers cap how many a page may hold — for a picture shown for twenty
     seconds. The loop runs on rAF because the game's own Loop has not started yet. */
  let previewing = true;
  let previewLast = performance.now();
  const spinPreview = (): void => {
    if (!previewing) return;
    const now = performance.now();
    const dt = Math.min(0.1, (now - previewLast) / 1000);
    previewLast = now;
    if (renderer.resize()) camera.resize(renderer.width, renderer.height);
    preview.resize(renderer.width, renderer.height);
    /* Measured every frame off the real panel, so it tracks the CSS breakpoint, the window
       being resized and the panel growing when step two opens, with nothing to keep in sync. */
    preview.setSafeArea(landing.safeLeftFraction(), landing.safeTopFraction());
    preview.update(dt, now / 1000);
    if (preview.hasMesh) renderer.renderer.render(preview.scene, preview.camera);
    requestAnimationFrame(spinPreview);
  };
  requestAnimationFrame(spinPreview);

  /* Every dial move lands here. The build is on the worker pool, and a token guards the race:
     drag a slider and several builds are in flight at once, arriving in whatever order they
     finish rather than the order they were asked for. Without this the preview settles on
     whichever happened to be slowest, which is usually not the one under the player's hand. */
  let previewToken = 0;
  landing.onSpec = (spec) => {
    const token = ++previewToken;
    void mesh.buildSpec(spec)
      .then((built) => {
        if (token !== previewToken || !previewing) {
          built.geometry.dispose();
          return;
        }
        preview.setMesh(built);
      })
      .catch((err: unknown) => console.error('[preview] build failed', err));
  };

  const session = await landing.choose();
  previewing = false;
  previewToken++;          // strand any build still in flight
  preview.dispose();
  swim.setSpec(session.spec);
  swim.setBay(session.bay, session.listed);

  /* The settings panel owns the audio mixer and, now, the only listing of the controls. It
     restores the saved volumes in its own constructor, so it is built before anything is
     audible rather than after. */
  /* Who got the feeder, said out loud for a moment. Without this the prize appears on somebody
     with no explanation, and the one thing in the game you can win passes unremarked. */
  const flash = document.createElement('div');
  flash.className = 'flash';
  overlay.appendChild(flash);
  let flashText = '';

  const menu = new Menu(overlay, audio);
  menu.setShark(session.spec.name, session.spec.species);

  /* Phone controls. Decided by whether the pointer can hover rather than by sniffing the user
     agent — a Surface with a keyboard folded back is a touch device and a phone plugged into a
     monitor is not, and only one of those two tests gets that right. ?touch=1 forces them on
     for testing on a desktop. */
  const wantsTouch = Input.coarsePointer
    || new URLSearchParams(location.search).has('touch');
  const touch = wantsTouch ? new TouchControls(overlay, input, camera) : null;
  if (wantsTouch) menu.useTouchUi();

  let seed = readSeed(session.bay);
  /* The species gallery is a Phase 1 turntable, not part of the game. It used to be one Tab
     press away at all times, which is one mis-hit away at all times. It keeps working and it
     keeps being useful for looking at a seed, but you now have to ask for it by URL. */
  let active: Mode = new URLSearchParams(location.search).has('lab') ? lab : swim;

  function setSeed(next: number): void {
    seed = next >>> 0;
    const url = new URL(location.href);
    url.searchParams.set('seed', String(seed));
    history.replaceState(null, '', url);
    swim.setSeed(seed);
    lab.setSeed(seed);
  }

  lab.onSeedRequest = setSeed;
  bus.emit('mode:change', { mode: active.name });

  /* The menu key lives here rather than in Input, because it has to work while a DOM control
     has focus and Input deliberately refuses to fire then.

     Checked BEFORE the text-field guard exempts anything, but only for non-text controls: a
     range slider keeps focus after a drag, and a menu you cannot close with the same key that
     opened it because you happened to touch a slider is a trap.

     Species cycling on [ and ] is gone: which animal you are is chosen on the landing page
     now, and a key that silently rebuilt your shark mid-swim would also desync every peer,
     since they were told what you are exactly once. */
  window.addEventListener('keydown', (e) => {
    const el = document.activeElement;
    const typing = el instanceof HTMLTextAreaElement
      || (el instanceof HTMLInputElement && el.type !== 'range');
    if (typing) return;
    if (e.code === 'KeyM') {
      menu.toggle();
      return;
    }
  });

  const rows: Array<[string, string]> = [];
  const snapshot: DebugSnapshot = {
    fps: 0,
    frameMs: 0,
    updateMs: 0,
    renderMs: 0,
    steps: 0,
    history: new Float32Array(0),
    historyHead: 0,
    drawCalls: 0,
    triangles: 0,
    programs: 0,
    geometries: 0,
    mode: 'swim',
    rows,
  };

  const loop = new Loop({
    beginFrame() {
      input.beginFrame();
      if (input.consume('debug')) debug.toggle();
      if (input.consume('regen')) setSeed((seed * 1664525 + 1013904223) >>> 0);
      if (renderer.resize()) camera.resize(renderer.width, renderer.height);
    },

    fixedUpdate(dt) {
      active.fixedUpdate(dt);
    },

    render(alpha, dt, time) {
      active.render(alpha, dt, time);
      renderer.render(camera.camera);
      // for next frame's aim: where the cursor actually lands on the seafloor
      camera.groundPoint(input.pointerNdc.x, input.pointerNdc.y, input.pointerWorld);
      menu.update(loop.fps);
      menu.setBay(swim.bayCode, swim.peerCount, swim.bayJoined);

      const who = swim.feederFlash();
      if (who !== flashText) {
        flashText = who;
        flash.textContent = who === '' ? '' : `${who} GOT THE FEEDER`;
        flash.classList.toggle('is-on', who !== '');
      }

      if (!debug.open) return;
      const stats = renderer.stats;
      rows.length = 0;
      rows.push(['seed', String(seed)]);
      rows.push(['res', `${renderer.width}x${renderer.height} @${renderer.pixelRatio.toFixed(2)}`]);
      for (const r of active.debugRows()) rows.push(r);
      snapshot.fps = loop.fps;
      snapshot.frameMs = loop.frameMs;
      snapshot.updateMs = loop.updateMs;
      snapshot.renderMs = loop.renderMs;
      snapshot.steps = loop.steps;
      snapshot.history = loop.history;
      snapshot.historyHead = loop.historyHead;
      snapshot.drawCalls = stats.drawCalls;
      snapshot.triangles = stats.triangles;
      snapshot.programs = stats.programs;
      snapshot.geometries = stats.geometries;
      snapshot.mode = active.name;
      debug.update(snapshot);
    },

    endFrame() {
      input.endFrame();
    },
  });

  window.addEventListener('resize', () => {
    if (renderer.resize()) camera.resize(renderer.width, renderer.height);
  });

  /**
   * Play the shot list and record it.
   *
   * A LOOP OF ITS OWN, not the game's. The reel advances by exactly one frame's worth of
   * simulated time per drawn frame whatever the clock says, so a kelp replant or a shark
   * meshing on a worker costs wall-clock seconds and zero recorded frames. Driving this off the
   * real Loop would bake every hitch into the file, and the hitches are what a viewer notices.
   */
  async function runReel(
    format: ReelFormat, cv: HTMLCanvasElement, ov: HTMLElement,
  ): Promise<void> {
    document.body.classList.add('is-reel');
    const size = FORMATS[format];
    cv.style.width = `${size.w}px`;
    cv.style.height = `${size.h}px`;
    /* 1, not devicePixelRatio. The file has to be exactly 1080 across, not 1080 times whatever
       display it happened to be captured on — otherwise the same command gives a different
       master on every machine. */
    renderer.setPixelRatioCap(1);
    renderer.resize();
    camera.resize(size.w, size.h);

    const hud = document.createElement('div');
    hud.className = 'reel-hud';
    ov.appendChild(hud);

    if (!recordingSupported()) {
      hud.textContent = 'THIS BROWSER CANNOT RECORD A CANVAS — RUN IT IN CHROME';
      return;
    }

    /* The reel's OWN seed. `seed` up there is bound after the landing page resolves, and the
       reel returns long before that — reaching for it here read a `let` still in its temporal
       dead zone, which is a hard throw at boot and not a subtle one.

       readSeed('') is the right source anyway: no bay, so it takes ?seed= if there is one and
       generates otherwise, which is exactly what "?reel=9x16&seed=1337 pins the world" means. */
    const reelSeed = readSeed('');
    swim.setBay('', false);
    swim.setSpec(defaultSpec(reelSeed));
    swim.enter(reelSeed);

    const reel = new Reel(format);
    const rec = new Recorder(cv, { filename: `shoreplay-${format}` });
    const world = {
      setSkyPhase: (p: number) => swim.setSkyPhase(p),
      setSkyRate: (r: number) => swim.setSkyRate(r),
      setCompanions: (n: number) => swim.setCompanions(n),
      place: (x: number, z: number, h: number) => swim.placePlayer(x, z, h),
      ready: () => swim.worldReady(),
    };

    const step = 1 / REEL_FPS;
    let started = false;
    let t = 0;

    await new Promise<void>((resolve) => {
      const frame = (): void => {
        // The simulation runs at the capture rate, so what is filmed is what was simulated.
        swim.fixedUpdate(step);
        t += step;
        swim.driveCompanions(t);
        swim.render(1, step, t);

        const live = reel.step(world);
        renderer.render(camera.camera);

        /* The card goes over the scene INTO THE SAME BUFFER, with autoClear off so it composites
           rather than wiping what was just drawn. This is the only reason the cards end up in
           the file at all — captureStream records the canvas, and a DOM overlay is not on it. */
        if (reel.cards.update(step)) {
          renderer.renderer.autoClear = false;
          renderer.renderer.render(reel.cards.scene, reel.cards.camera);
          renderer.renderer.autoClear = true;
        }

        if (live) {
          if (!started) {
            started = rec.start();
            hud.classList.add('is-rec');
          }
          rec.capture();
          hud.textContent =
            `REC ${(reel.progress * REEL_SECONDS).toFixed(1)}/${REEL_SECONDS}s · ${reel.shotLabel}`;
        } else {
          hud.textContent = 'BUILDING THE WORLD…';
        }

        if (reel.done) {
          resolve();
          return;
        }
        requestAnimationFrame(frame);
      };
      requestAnimationFrame(frame);
    });

    hud.textContent = `ENCODING ${rec.frameCount} FRAMES…`;
    hud.classList.remove('is-rec');
    await rec.stop();
    reel.dispose();
    hud.textContent = 'SAVED — CHECK YOUR DOWNLOADS';
  }

  function teardown(): void {
    loop.stop();
    swim.dispose();
    lab.dispose();
    mesh.dispose();
    input.dispose();
    debug.dispose();
    flash.remove();
    menu.dispose();
    touch?.dispose();
    landing.dispose();
    previewing = false;
    preview.dispose();
    renderer.dispose();
  }

  window.addEventListener('beforeunload', teardown);

  // Vite re-executes this module on every hot update. Without an explicit teardown each
  // update leaks a WebGLRenderer plus a worker pool, and the browser reaps the oldest GL
  // context once a handful have piled up — which presents as a canvas that silently goes
  // white with no error anywhere.
  import.meta.hot?.dispose(teardown);

  camera.resize(renderer.width, renderer.height);
  active.enter(seed);
  loop.start();

  /* First visit only, and never for someone who arrived on an invite link — they were sent
     here by a person, not by a menu, and the first thing they should see is the reef. */
  const forced = new URLSearchParams(location.search).has('nudge');
  if (bayFromUrl() === '' || forced) menu.showNudge(forced);
}

// boot() is async now — it waits on the landing page — so a throw inside it lands here as a
// rejection rather than as an exception, and try/catch around the call would never see it.
boot().catch((err: unknown) => {
  fatal(`SHOREPLAY failed to start: ${(err as Error).message}`);
  throw err;
});
